// src/domains/cards.ts —— cards 域（原生）：首页 / 我的卡 / 选卡段 / 开卡 / 卡详情 / 卡操作（06 U-02～U-06c、U-17；TD-824 不读写原型假数据键）
import { ApiError } from '../../../shared/src/api/client';
import type { CardDetail, CardItem, CardProduct, CardSecret, CardSubmit, SmsCode, UserPage, Wallet } from '../../../shared/src/api/types';
import { authCancelFeeText } from '../../../shared/src/fees';
import { cmp, fmtAmount, fmtOriginal, fromMicro, isZero, max as mmax, parseDecimalInput, sub, toMicro, type Micro } from '../../../shared/src/money';
import { CursorList } from '../../../shared/src/page';
import { createPoller, type Poller } from '../../../shared/src/poll';
import { readLocal, writeLocal } from '../../../shared/src/storage';
import { getTg } from '../../../shared/src/tg';
import { fmtDateTime } from '../../../shared/src/time';
import { I18N_CARDS, REWRITES_CARDS } from '../i18n/cards';
import type { H5State, Screen } from '../state';
import type { BillEntry, BillFee, BillFilter, CardsSlice, CardWithdrawFees, OpenForm, TxDetail } from './cards.types';
import { KEYS, MODAL_KEYS } from './cards.keys';
import { addrView, holderFieldError, isUsArea, pickCity, pickState, usAddrError, zipDigits } from './billaddr';
import { legalParts } from './legal';
import { listFlags } from './listview';
import { openTicketsTab } from './me';
import { str, type H5Ctx, type H5Domain, type InputEvent } from './types';

declare module '../state' {
  interface H5Slices {
    c: CardsSlice;
  }
}

type Dict = Readonly<Record<string, unknown>>;

interface ScrollEvent {
  currentTarget?: { scrollLeft?: number } | null;
  target?: { scrollLeft?: number } | null;
}

// 原型 L526 FACES 逐字
const FACES: Readonly<Record<string, string>> = {
  black: 'linear-gradient(135deg,#0f1a17 0%,#2a3833 60%,#1b2622 100%)',
  green: 'linear-gradient(135deg,#0b5c41 0%,#12a874 60%,#39c88f 100%)',
  purple: 'linear-gradient(135deg,#2e1f7a 0%,#5b3fd6 60%,#8a6cf5 100%)',
  red: 'linear-gradient(135deg,#8a1a3b 0%,#d1434b 60%,#f07a4a 100%)',
};
const faceOf = (k: string): string => FACES[k] ?? FACES.black ?? '';

// P-19：CLOSED / FAILED 不进首页轮播；只读态（U-02a）
const HIDDEN_IN_CAROUSEL = new Set(['CLOSED', 'FAILED']);
// U-22 E-05：存在这些状态或 pending_action 非空时轮询
const POLL_STATUSES = new Set(['PENDING', 'PRECLOSE']);
// U-05：顶栏显示 / 隐藏与 4 宫格不渲染的状态（PENDING S-49 + 只读态）
const NO_OPS = new Set(['PENDING', 'CLOSED', 'FAILED']);
const HOLDER_RE = /^[A-Za-z][A-Za-z .'-]{0,49}$/; // C-15
const CARD_STEP_PX = 314; // E-02：卡宽 300 + gap 14
const HOME_POLL_MS = 15_000;
const DETAIL_POLL_MS = 15_000;
const SMS_POLL_MS = 10_000;
const HOME_BILL_SIZE = 5; // 原型 L769 首页每页 5
const CARD_TX_SIZE = 20; // E-04
const PRIMED_MS = 1_000; // TD-838：轮询器建立后此窗口内的首跑与刚完成的请求重复，跳过；隐藏时建立、回前台才首跑的不跳过
const FREEZE_WATCH_MS = 120_000; // 冻结 / 解冻受理后等待读模型终态的上限：超时静默放弃（不误报失败），离开卡详情即放弃
const CHIPS = [50, 100, 200, 500] as const;

const F0: OpenForm = { first: '', last: '', street: '', city: '', state: '', zip: '', amount: '', cityManual: false };
export const C0: CardsSlice = {
  cards: null, products: null, selected: null, feeOpen: null, form: F0, filter: 'all', bills: null, billsRev: 0, curId: null, detail: null, cardTx: null,
  secret: null, reveal: false, sms: [], modalAmount: '', withdrawFee: '0', balance: '0', dotIndex: 0, termsClosed: false,
};

// 02 §4.3 area → 国家（backend cardcore/country.go 英文名；中文为通行译名）
const REGIONS: Readonly<Record<string, readonly [string, string]>> = {
  US: ['美国', 'United States'], HK: ['香港', 'Hong Kong'], GB: ['英国', 'United Kingdom'], SG: ['新加坡', 'Singapore'], AE: ['阿联酋', 'United Arab Emirates'],
  AR: ['阿根廷', 'Argentina'], AT: ['奥地利', 'Austria'], AU: ['澳大利亚', 'Australia'], BE: ['比利时', 'Belgium'], BG: ['保加利亚', 'Bulgaria'],
  BR: ['巴西', 'Brazil'], CA: ['加拿大', 'Canada'], CH: ['瑞士', 'Switzerland'], CL: ['智利', 'Chile'], CN: ['中国', 'China'], CO: ['哥伦比亚', 'Colombia'],
  CY: ['塞浦路斯', 'Cyprus'], CZ: ['捷克', 'Czechia'], DE: ['德国', 'Germany'], DK: ['丹麦', 'Denmark'], EE: ['爱沙尼亚', 'Estonia'], ES: ['西班牙', 'Spain'],
  FI: ['芬兰', 'Finland'], FR: ['法国', 'France'], GR: ['希腊', 'Greece'], HR: ['克罗地亚', 'Croatia'], HU: ['匈牙利', 'Hungary'], ID: ['印度尼西亚', 'Indonesia'],
  IE: ['爱尔兰', 'Ireland'], IL: ['以色列', 'Israel'], IN: ['印度', 'India'], IS: ['冰岛', 'Iceland'], IT: ['意大利', 'Italy'], JP: ['日本', 'Japan'],
  KR: ['韩国', 'South Korea'], LI: ['列支敦士登', 'Liechtenstein'], LT: ['立陶宛', 'Lithuania'], LU: ['卢森堡', 'Luxembourg'], LV: ['拉脱维亚', 'Latvia'],
  MO: ['澳门', 'Macao'], MT: ['马耳他', 'Malta'], MX: ['墨西哥', 'Mexico'], MY: ['马来西亚', 'Malaysia'], NL: ['荷兰', 'Netherlands'], NO: ['挪威', 'Norway'],
  NZ: ['新西兰', 'New Zealand'], PE: ['秘鲁', 'Peru'], PH: ['菲律宾', 'Philippines'], PL: ['波兰', 'Poland'], PT: ['葡萄牙', 'Portugal'], RO: ['罗马尼亚', 'Romania'],
  SA: ['沙特阿拉伯', 'Saudi Arabia'], SE: ['瑞典', 'Sweden'], SI: ['斯洛文尼亚', 'Slovenia'], SK: ['斯洛伐克', 'Slovakia'], TH: ['泰国', 'Thailand'],
  TR: ['土耳其', 'Turkey'], TW: ['台湾', 'Taiwan'], VN: ['越南', 'Vietnam'], ZA: ['南非', 'South Africa'],
};

function regionName(area: string, lang: 'zh' | 'en'): string {
  const r = REGIONS[area.trim().toUpperCase()];
  return r ? (lang === 'zh' ? r[0] : r[1]) : area;
}

function countryName(en: string, lang: 'zh' | 'en'): string {
  if (lang === 'en') return en;
  const hit = Object.values(REGIONS).find((r) => r[1] === en);
  return hit ? hit[0] : en;
}

// ---------- 模块级运行态（单实例宿主；mount 时整体复位，防上一宿主的轮询串入） ----------
let homePoll: Poller | null = null;
let detailPoll: Poller | null = null;
let smsPoll: Poller | null = null;
let gen = 0; // 会话代号：session:ready / lost 递增，旧会话的在途结果丢弃
let revealing = false;
let sessionOn = false; // session:ready 之后、session:lost 之前
let homeLoading = false; // 首屏（卡列表 + 账单首页）在途：期间进入 home 不重复拉 GET /cards
let freezeWatch: { id: number; from: string; seen: boolean; at: number } | null = null;
let unVisibility: (() => void) | null = null;
let unForeground: (() => void) | null = null;
let cardsSeq = 0; // GET /cards 请求序号：轮询与进入首页并发时，只采纳最后发出的请求的应答

const S = (ctx: H5Ctx): H5State => ctx.host.state;
const T = (ctx: H5Ctx): Dict => ctx.gp.ui.dict();

function setC(ctx: H5Ctx, patch: Partial<CardsSlice>): void {
  ctx.host.setState({ c: { ...ctx.host.state.c, ...patch } });
}

// D8-12：两位向零截断 + 千分位；API 金额畸形时 fmtAmount 抛错（不静默吞）
const money2 = (v: string | null | undefined): string => fmtAmount(v ?? '0', 2);
const absAmt = (v: string | null | undefined): string => (v && v.startsWith('-') ? v.slice(1) : v ?? '0');
// 卡交易金额：USD 只用 $ 前缀（不再拼「$x USD」）；非 USD 交易不加 $，以币种代码后缀
function cardAmt(v: string | null | undefined, currency: string | null | undefined): string {
  const cur = (currency ?? 'USD').trim().toUpperCase() || 'USD';
  return cur === 'USD' ? `$${money2(v)}` : `${money2(v)} ${cur}`;
}
// 外币消费原币金额（「176,000 VND」，shared fmtOriginal）
const origAmt = fmtOriginal;
// D14：交易手续费从 USDT 余额扣除，对客一律以 USDT 计
const usdtAmt = (v: string | null | undefined): string => `${money2(v)} USDT`;

// 配置下发的数值（string | number）宽松格式化：非法值显示原样，不让整页抛错
function cfgMoney(v: unknown): string {
  if (typeof v !== 'string' && typeof v !== 'number') return '';
  try {
    return fmtAmount(String(v), 2);
  } catch {
    return String(v);
  }
}

// 十进制串去尾零：'2.500000' → '2.5'；'50.000000' → '50'
function plain(v: string): string {
  return fromMicro(toMicro(v)).replace(/\.?0+$/, '');
}

// 费率百分数：'2.5000' → '2.5'
const pct = (v: string): string => plain(v);

// U-06b 手续费：至少两位，配置超过两位小数时按原值最多 6 位
function feeText(v: string): string {
  const [i = '0', f = ''] = fromMicro(toMicro(v)).split('.');
  const trimmed = f.replace(/0+$/, '');
  return `${fmtAmount(i, 0)}.${trimmed.length > 2 ? trimmed : trimmed.padEnd(2, '0')}`;
}

function micro(v: string | null | undefined): Micro {
  try {
    return toMicro(v ?? '0');
  } catch {
    return 0n;
  }
}

// 用户输入金额：非法 / 超位数视为 0（实时计算用；提交另行校验）
function inputMicro(raw: string): Micro {
  const s = parseDecimalInput(raw, 6);
  return s === null ? 0n : toMicro(s);
}

const brandText = (b: string): string => (b === 'MASTERCARD' ? 'MC' : b);
const maskNo = (last4: string | null): string => '**** **** **** ' + (last4 ?? '----');
const groupNo = (n: string): string => n.replace(/(\d{4})/g, '$1 ').trim();

// 全局约定「卡状态」7 态
const CARD_STATUS_KEY: Readonly<Record<string, string>> = {
  ACTIVE: 'stActive', INACTIVE: 'stFrozen', PENDING: 'stPending2', RECHARGE: 'stRecharging', PRECLOSE: 'stClosing', CLOSED: 'stClosed', FAILED: 'stFailed',
};
function cardStatusText(t: Dict, s: string): string {
  return str(t, CARD_STATUS_KEY[s] ?? '') || s;
}

// 原型 L607 statusStyle + 新增 4 态底色（全局约定「卡状态」）
function statusStyle(s: string): string {
  const bg: Readonly<Record<string, string>> = { ACTIVE: 'rgba(57,200,143,.25)', INACTIVE: 'rgba(255,255,255,.25)', CLOSED: 'rgba(255,255,255,.25)', FAILED: 'rgba(209,67,75,.45)' };
  return `padding:2px 8px;border-radius:999px;font-size:11px;font-weight:700;background:${bg[s] ?? 'rgba(255,200,80,.3)'}`;
}

// 全局约定浅底章变体（我的卡列表；色板同原型 L643）
function chipStyle(s: string): string {
  const c: Readonly<Record<string, readonly [string, string]>> = {
    ACTIVE: ['#e3f6ee', '#0d9a68'], INACTIVE: ['#eef3f0', '#6b7772'], CLOSED: ['#eef3f0', '#6b7772'], FAILED: ['#fdecec', '#d1434b'],
  };
  const [bg, fg] = c[s] ?? ['#fff3df', '#c47a0c'];
  return `font-size:11px;border-radius:999px;padding:3px 10px;font-weight:700;background:${bg};color:${fg}`;
}

// 原型 L606 faceStyle + 全局约定卡面处理
function faceStyle(c: { face_color: string; status: string }, big: boolean): string {
  const filter = c.status === 'CLOSED' ? 'filter:grayscale(1);opacity:.6' : ['INACTIVE', 'PRECLOSE', 'FAILED'].includes(c.status) ? 'filter:grayscale(.7);opacity:.85' : '';
  return `flex:none;width:${big ? '100%' : '300px'};height:${big ? '200px' : '180px'};border-radius:20px;padding:${big ? '22px' : '18px'};color:#fff;background:${faceOf(c.face_color)};display:flex;flex-direction:column;justify-content:space-between;scroll-snap-align:start;cursor:pointer;position:relative;overflow:hidden;box-shadow:0 14px 30px rgba(15,26,23,.18);${filter}`;
}

// 原型 L182–185 宫格按钮样式逐字；06 U-05 禁用态（可见但不满足前置条件 / 在途）追加 opacity:.45;cursor:not-allowed
const OPS_BTN = 'border:0;background:#fff;border-radius:16px;padding:12px 4px;display:flex;flex-direction:column;align-items:center;gap:8px;cursor:pointer';
const OPS_BTN_OFF = 'border:0;background:#fff;border-radius:16px;padding:12px 4px;display:flex;flex-direction:column;align-items:center;gap:8px;opacity:.45;cursor:not-allowed';
function opsBtns(d: CardDetail | null): Record<'recharge' | 'withdraw' | 'freeze' | 'close', string> {
  const ok = (allowed: readonly string[]): string => (d && !d.pending_action && allowed.includes(d.status) ? OPS_BTN : OPS_BTN_OFF);
  return { recharge: ok(['ACTIVE']), withdraw: ok(['ACTIVE', 'INACTIVE']), freeze: ok(['ACTIVE', 'INACTIVE']), close: ok(['ACTIVE', 'INACTIVE']) };
}

// 详情未到达时的大卡面骨架（gpSkel，U-20 色板）
const FACE_SKELETON = 'width:100%;height:200px;border-radius:20px;background:#e6ebe8;animation:gpSkel 1.1s ease-in-out infinite';

// 原型 L608 pill 逐字
function pill(active: boolean): string {
  return `height:30px;padding:0 12px;border:0;border-radius:999px;font-size:12px;font-weight:700;cursor:pointer;background:${active ? '#0f1a17' : '#fff'};color:${active ? '#fff' : '#6b7772'};box-shadow:0 1px 2px rgba(0,0,0,.05)`;
}

// 原型 L633 chip 逐字 + 低于 min_amount 的禁用后缀（U-04）
function chip(active: boolean, disabled = false): string {
  return `flex:1;height:38px;border:1px solid ${active ? '#0d9a68' : '#dfe5e2'};border-radius:10px;background:${active ? '#e3f6ee' : '#fff'};color:${active ? '#0d9a68' : '#0f1a17'};font-weight:700;font-size:13px;cursor:pointer${disabled ? ';opacity:.4;pointer-events:none' : ''}`;
}

// E-02 圆点（原型 L104 两种圆点样式逐字）
// U-22 E-02：当前 18×6、非当前 6×6，宽度变化 transition:width .2s [规格补全]
const DOT_ON = 'width:18px;height:6px;border-radius:3px;background:#0f1a17;display:block;transition:width .2s';
const DOT_OFF = 'width:6px;height:6px;border-radius:3px;background:#c9d2ce;display:block;transition:width .2s';

// 全局约定「交易状态」表：[图标, 图标底色, 图标字色, 状态行色, 金额符号, 金额色, 删除线]
type TxStyle = readonly [string, string, string, string, '+' | '-', string, boolean];
const TX_UNKNOWN: TxStyle = ['▤', '#f1f4f2', '#8a958f', '#8a958f', '-', '#0f1a17', false];
const TX_STYLE: Readonly<Record<string, TxStyle>> = {
  Authorize: ['▤', '#f1f4f2', '#0f1a17', '#c47a0c', '-', '#0f1a17', false],
  Settle: ['▤', '#f1f4f2', '#0f1a17', '#0d9a68', '-', '#0f1a17', false],
  AuthorizeDeclined: ['!', '#fdecec', '#d1434b', '#d1434b', '-', '#d1434b', true],
  Auth_Delete: ['▤', '#f1f4f2', '#8a958f', '#8a958f', '-', '#8a958f', true],
  Refund: ['↺', '#e3f6ee', '#0d9a68', '#0d9a68', '+', '#0d9a68', false],
  RefundCancell: ['↺', '#fdecec', '#d1434b', '#d1434b', '-', '#d1434b', false],
  Dispute: ['!', '#fff3df', '#c47a0c', '#c47a0c', '-', '#0f1a17', false],
  Unknown: TX_UNKNOWN,
};
const TX_STATUS_KEY: Readonly<Record<string, string>> = {
  Authorize: 'stAuth', Settle: 'stSettle', AuthorizeDeclined: 'stDeclined', Auth_Delete: 'stAuthDelete', Refund: 'stRefund', RefundCancell: 'stRefundCancel', Dispute: 'stDispute', Unknown: 'stUnknown',
};

// 原型 L601 iconStyle 逐字
function iconStyle(bg: string, fg: string): string {
  return `width:38px;height:38px;border-radius:12px;background:${bg};color:${fg};display:inline-flex;align-items:center;justify-content:center;font-weight:800;font-size:14px;flex:none`;
}

function amountStyle(color: string, strike: boolean): string {
  return `font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:${color};${strike ? 'text-decoration:line-through;opacity:.7' : ''}`;
}

// 全局约定类型规则：组最新 status=Authorize → 预授权；其余按 transaction_type
function txTypeText(t: Dict, status: string, type: string | null): string {
  if (status === 'Authorize') return str(t, 'typeAuth');
  return type === 'Refund' ? str(t, 'typeRefund') : str(t, 'typePurchase');
}

export interface BillRow {
  key: string;
  merchant: string;
  time: string;
  card: string;
  icon: string;
  iconStyle: string;
  amountText: string;
  amountStyle: string;
  typeText: string;
  statusText: string;
  statusStyle: string;
  feeSub: string;
  hasFeeSub: boolean;
  feeLine: string;
  hasFeeLine: boolean;
  // 外币消费原币副标题（「176,000 VND」）：billing_amount 已是卡币种金额，original_* 仅外币消费时下发（2026-10-19）
  origSub: string;
  hasOrigSub: boolean;
  open: () => void;
}

// U-02 账单行：tx 行金额不并入手续费（D14 / S-58），Fee 行独立
function mapBill(t: Dict, e: BillEntry, open: (e: BillEntry) => void): BillRow {
  if (e.entry === 'fee') {
    const owed = micro(e.owed) > 0n;
    return {
      key: 'f' + e.id,
      merchant: e.merchant ?? '',
      time: fmtDateTime(e.created_at, { seconds: true }),
      card: e.last4 ? '•••• ' + e.last4 : '',
      icon: '%',
      iconStyle: iconStyle('#fff3df', '#0f1a17'),
      amountText: `-${usdtAmt(absAmt(e.fee))}`,
      amountStyle: amountStyle('#0f1a17', false),
      typeText: str(t, 'typeFee'),
      statusText: str(t, 'stSettle'),
      statusStyle: 'font-size:11px;margin-top:2px;color:#0d9a68',
      feeSub: '',
      hasFeeSub: false,
      feeLine: owed ? str(t, 'feeOwedAdded').replace('{owed}', money2(e.owed)) : '',
      hasFeeLine: owed,
      origSub: '',
      hasOrigSub: false,
      open: () => open(e),
    };
  }
  const [icon, bg, fg, stColor, sign, amtColor, strike] = TX_STYLE[e.status] ?? TX_UNKNOWN;
  const fee = e.fee_charged && !isZero(micro(e.fee_charged)) ? usdtAmt(absAmt(e.fee_charged)) : '';
  const orig = origAmt(e.original_amount, e.original_currency);
  return {
    key: 't' + e.id,
    merchant: e.merchant_name ?? '',
    time: fmtDateTime(e.transaction_date, { seconds: true }),
    card: e.last4 ? '•••• ' + e.last4 : '',
    icon,
    iconStyle: iconStyle(bg, fg),
    amountText: `${sign}${cardAmt(absAmt(e.billing_amount), e.billing_currency)}`,
    amountStyle: amountStyle(amtColor, strike),
    typeText: txTypeText(t, e.status, e.transaction_type),
    statusText: str(t, TX_STATUS_KEY[e.status] ?? 'stUnknown'),
    statusStyle: `font-size:11px;margin-top:2px;color:${stColor}`,
    feeSub: fee ? `${str(t, 'fee')} ${fee}` : '',
    hasFeeSub: !!fee,
    feeLine: '',
    hasFeeLine: false,
    origSub: orig,
    hasOrigSub: orig !== '',
    open: () => open(e),
  };
}

// D14 零值规则：费率与固定费均为 0 的项不渲染；某行无项时整行不渲染；销卡费为 0 仍渲染（D12）
// 体验评审 2026-10-15 #7：销卡费不与开卡费并排置顶，移到末行并弱化（style 降低不透明度）
const FEE_ITEM_MUTED = 'opacity:.65';
interface FeeItem {
  text: string;
  style: string;
}
interface FeeRow {
  items: FeeItem[];
}

// 比例 + 固定组合：任一为 0 只写非零部分（用户反馈 2026-10-16：「0%+0.50 USDT」冗余）；两者皆 0 由调用方按零值规则不渲染
function rateFixed(rate: string, fixed: string, sep: string): string {
  const r = !isZero(micro(rate));
  const f = !isZero(micro(fixed));
  if (r && f) return `${pct(rate)}%${sep}${usdtAmt(fixed)}`;
  return r ? `${pct(rate)}%` : usdtAmt(fixed);
}

// 用户拍板 2026-10-15：各项手续费从 USDT 余额扣，一律写 USDT（卡额度 / 卡消费才写 $）
function feeRows(t: Dict, p: CardProduct): FeeRow[] {
  const nz = (...v: string[]): boolean => v.some((x) => !isZero(micro(x)));
  const item = (text: string): FeeItem => ({ text, style: '' });
  // 0040：授权撤销费按「[比例% + ]每笔固定 USDT（撤销金额 ≥ 门槛 USD 时收取）」组合，皆 0 不渲染
  const authCancel = authCancelFeeText(p, { fixed: str(t, 'authCancelFixed'), cond: str(t, 'authCancelCond') });
  const rows: FeeItem[][] = [
    [item(`${str(t, 'openFee')} ${usdtAmt(p.open_fee)}`)],
    [
      ...(nz(p.tx_fee_rate, p.tx_fee_fixed) ? [item(`${str(t, 'txFee')} ${rateFixed(p.tx_fee_rate, p.tx_fee_fixed, '+')}`)] : []),
      ...(nz(p.cross_border_rate, p.cross_border_fixed) ? [item(`${str(t, 'crossBorderFee')} ${rateFixed(p.cross_border_rate, p.cross_border_fixed, '+')}`)] : []),
    ],
    [
      ...(nz(p.declined_fee) ? [item(`${str(t, 'declinedFee')} ${usdtAmt(p.declined_fee)}`)] : []),
      ...(nz(p.dispute_fee) ? [item(`${str(t, 'disputeFee')} ${usdtAmt(p.dispute_fee)}`)] : []),
    ],
    [
      ...(nz(p.refund_fee_rate) ? [item(`${str(t, 'refundFee')} ${pct(p.refund_fee_rate)}%`)] : []),
      ...(authCancel ? [item(`${str(t, 'authCancelFee')} ${authCancel}`)] : []),
    ],
    p.capabilities.close === false ? [] : [{ text: `${str(t, 'closeFee')} ${usdtAmt(p.close_fee)}`, style: FEE_ITEM_MUTED }],
  ];
  return rows.filter((r) => r.length > 0).map((items) => ({ items }));
}

// 选卡页费率折叠：首行（开卡费）+ 末行（销卡费，弱化）常显；中间各行折叠。无可折叠行时不出「查看全部费率」
function foldFees(t: Dict, rows: FeeRow[], open: boolean): { feeRows: FeeRow[]; hasMoreFees: boolean; moreFeesText: string } {
  const hasMoreFees = rows.length > 2;
  const shown = !hasMoreFees || open ? rows : [rows[0], rows[rows.length - 1]].filter((r): r is FeeRow => r !== undefined);
  return { feeRows: shown, hasMoreFees, moreFeesText: str(t, open ? 'lessFees' : 'moreFees') };
}

// F-04：卡转出到账 = max(0, amount − card_withdraw_fee)
// 用户 2026-10-20：卡转出上限取服务端 withdraw_max（扣暂扣、保留 0.01，与 1207 withdraw_exceeds 同口径）。
function wdMaxOf(d: CardDetail | null): string {
  return d ? d.withdraw_max : '0';
}

function withdrawNet(amount: Micro, fee: Micro): Micro {
  return mmax(0n, sub(amount, fee));
}

// 交易详情「原币金额」行：仅外币消费（original_* 非空）时追加（2026-10-19；金额行 billing_amount 已为卡币种金额）
function origRow(t: Dict, d: TxDetail): Array<readonly [string, string]> {
  const v = origAmt(d.original_amount, d.original_currency);
  return v ? [[str(t, 'origAmount'), v]] : [];
}

// ---------- 交易详情（U-17） ----------
async function openTxDetail(ctx: H5Ctx, id: number): Promise<void> {
  const my = gen;
  try {
    const d = await ctx.gp.api.call<TxDetail>('GET /api/user/transactions/{id}', { path: { id } });
    if (my !== gen) return;
    const t = T(ctx);
    const rows: Array<readonly [string, string]> = [
      [str(t, 'merchant'), `${d.merchant_name ?? ''} ${d.merchant_country ?? ''}`.trim()],
      [str(t, 'cardNo'), d.last4 ? '•••• ' + d.last4 : ''], // X-37：仅 last4
      [str(t, 'time'), fmtDateTime(d.transaction_date, { seconds: true })],
      [str(t, 'amount'), cardAmt(absAmt(d.billing_amount), d.billing_currency)],
      ...origRow(t, d),
      [str(t, 'fee'), usdtAmt(absAmt(d.fee_charged))],
      [str(t, 'type'), txTypeText(t, d.status, d.transaction_type)],
      [str(t, 'status'), str(t, TX_STATUS_KEY[d.status] ?? 'stUnknown')],
      ['ID', d.transaction_id],
    ];
    ctx.gp.ui.openModal({ type: 'tx', title: str(t, 'txDetail'), rows, txId: d.id, isFee: false });
  } catch (e) {
    if (my === gen) ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
  }
}

// U-17 对此笔申诉：POST /transactions/{id}/dispute（无体）→ 关交易详情弹层、跳消息中心工单页签、toastDispute（06 U-17）；
// 1012 = 同一交易已有处理中的申诉工单（cms.Dispute）；busy 互斥防双击双建；换会话后迟到的应答丢弃
async function disputeTx(ctx: H5Ctx): Promise<void> {
  const m = S(ctx).modal;
  const id = m?.type === 'tx' && m.isFee !== true && typeof m.txId === 'number' ? m.txId : null;
  if (id === null) return;
  const my = gen;
  await ctx.gp.ui.run('dispute', async () => {
    try {
      await ctx.gp.api.call('POST /api/user/transactions/{id}/dispute', { path: { id } });
      if (my !== gen) return;
      ctx.gp.ui.closeModal();
      openTicketsTab(ctx);
      ctx.gp.ui.toast(str(T(ctx), 'toastDispute'), 'success');
    } catch (e) {
      if (my !== gen) return;
      ctx.gp.ui.toast(e instanceof ApiError && e.code === 1012 ? str(T(ctx), 'h5wDisputeOpen') : ctx.gp.ui.err(e), 'error');
    }
  });
}

// U-17 Fee 变体：直接用列表条目数据（S-58），金额 0.00、手续费 = fee，无申诉按钮
function openFeeDetail(ctx: H5Ctx, e: BillFee): void {
  const t = T(ctx);
  const rows: Array<readonly [string, string]> = [
    [str(t, 'merchant'), e.merchant ?? ''],
    [str(t, 'cardNo'), e.last4 ? '•••• ' + e.last4 : ''],
    [str(t, 'time'), fmtDateTime(e.created_at, { seconds: true })],
    [str(t, 'amount'), cardAmt('0', 'USD')],
    [str(t, 'fee'), usdtAmt(absAmt(e.fee))],
    [str(t, 'type'), str(t, 'typeFee')],
    [str(t, 'status'), str(t, 'stSettle')],
    ['ID', e.id],
  ];
  // owed>0 追加「计入欠费」行；rows 下方注 feeNoRefundTip（S-45 一期不冲销）
  if (micro(e.owed) > 0n) rows.push([str(t, 'feeOwedLabel'), `${money2(e.owed)} USDT`]);
  ctx.gp.ui.openModal({ type: 'tx', title: str(t, 'txDetail'), rows, isFee: true, note: str(t, 'feeNoRefundTip') });
}

function openBill(ctx: H5Ctx, e: BillEntry): void {
  if (e.entry === 'fee') openFeeDetail(ctx, e);
  else void openTxDetail(ctx, e.id);
}

// ---------- 首页 ----------
function sortDesc(list: readonly CardItem[]): CardItem[] {
  return [...list].sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0));
}

function newBills(ctx: H5Ctx, filter: BillFilter): CursorList<BillEntry> {
  return new CursorList<BillEntry>((page) => ctx.gp.api.call<UserPage<BillEntry>>('GET /api/user/transactions', { query: { filter, page, size: HOME_BILL_SIZE } }));
}

function needsPoll(c: Pick<CardItem, 'status' | 'pending_action'>): boolean {
  return POLL_STATUSES.has(c.status) || !!c.pending_action;
}

function diffToast(ctx: H5Ctx, prev: readonly CardItem[], next: readonly CardItem[]): void {
  const t = T(ctx);
  for (const c of next) {
    const p = prev.find((x) => x.id === c.id);
    if (p?.status !== 'PENDING') continue;
    if (c.status === 'ACTIVE') ctx.gp.ui.toast(str(t, 'toastOpened'), 'success');
    else if (c.status === 'FAILED') ctx.gp.ui.toast(str(t, 'toastOpenFailed'), 'error');
  }
}

async function loadCards(ctx: H5Ctx, toastErr: boolean): Promise<void> {
  const my = gen;
  const seq = ++cardsSeq;
  try {
    const cards = await ctx.gp.api.call<CardItem[]>('GET /api/user/cards');
    if (my !== gen || seq !== cardsSeq) return; // 会话已换 / 已有更新的请求在途：丢弃旧应答
    const prev = S(ctx).c.cards;
    if (prev) diffToast(ctx, prev, cards);
    setC(ctx, { cards });
  } catch (e) {
    if (my !== gen || seq !== cardsSeq) return;
    if (toastErr) ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
    else console.warn('[gp:cards] 卡列表刷新失败', e);
  }
  managePolls(ctx);
}

async function reloadBills(ctx: H5Ctx, filter: BillFilter): Promise<void> {
  const my = gen;
  const bills = newBills(ctx, filter);
  setC(ctx, { filter, bills });
  await bills.loadFirst();
  if (my !== gen || S(ctx).c.bills !== bills) return;
  setC(ctx, { billsRev: S(ctx).c.billsRev + 1 });
  if (bills.error) ctx.gp.ui.toast(str(T(ctx), 'errNetwork'), 'error');
}

// U-20：首屏（卡列表 + 账单首页）就绪后发 home:ready 收起骨架；失败同样收起
async function loadHome(ctx: H5Ctx): Promise<void> {
  const my = gen;
  homeLoading = true;
  try {
    await Promise.all([loadCards(ctx, true), reloadBills(ctx, S(ctx).c.filter)]);
  } finally {
    if (my === gen) {
      homeLoading = false;
      ctx.events.emit('home:ready', {});
    }
  }
}

async function refreshBalance(ctx: H5Ctx): Promise<void> {
  const my = gen;
  try {
    const w = await ctx.gp.api.call<Wallet>('GET /api/user/wallet');
    if (my === gen) setC(ctx, { balance: w.balance });
  } catch (e) {
    if (my === gen) ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
  }
}

// ---------- 轮询（U-22 E-05 / TD-838） ----------
// createPoller 启动即跑一次；卡列表 / 详情刚取过，建立后 PRIMED_MS 内的首跑跳过，从一个间隔后开始。
// 页面隐藏时建立的轮询器首跑推迟到回前台，此时距建立已超窗口，照常执行（TD-838）
function deferredPoller(tick: () => Promise<unknown>, ms: number): Poller {
  let primedAt = Date.now();
  return createPoller(() => {
    if (primedAt > 0) {
      const fresh = Date.now() - primedAt < PRIMED_MS;
      primedAt = 0;
      if (fresh) return Promise.resolve();
    }
    return tick();
  }, ms);
}

function stopPolls(): void {
  homePoll?.stop();
  detailPoll?.stop();
  smsPoll?.stop();
  homePoll = detailPoll = smsPoll = null;
}

function curReadonly(s: H5State): boolean {
  const st = s.c.detail?.status ?? (s.c.cards ?? []).find((x) => x.id === s.c.curId)?.status;
  return !!st && HIDDEN_IN_CAROUSEL.has(st);
}

function managePolls(ctx: H5Ctx): void {
  const s = S(ctx);
  const homeNeed = (s.screen === 'home' || s.screen === 'myCards') && (s.c.cards ?? []).some(needsPoll);
  if (homeNeed && !homePoll) {
    homePoll = deferredPoller(() => loadCards(ctx, false), HOME_POLL_MS);
    homePoll.start();
  } else if (!homeNeed && homePoll) {
    homePoll.stop();
    homePoll = null;
  }
  const d = s.c.detail;
  const detailNeed = s.screen === 'cardDetail' && !!d && d.id === s.c.curId && needsPoll(d);
  if (detailNeed && !detailPoll) {
    detailPoll = deferredPoller(() => loadDetail(ctx, false), DETAIL_POLL_MS);
    detailPoll.start();
  } else if (!detailNeed && detailPoll) {
    detailPoll.stop();
    detailPoll = null;
  }
  const smsNeed = s.screen === 'cardDetail' && s.c.curId !== null && !curReadonly(s);
  if (smsNeed && !smsPoll) {
    smsPoll = createPoller(() => loadSms(ctx), SMS_POLL_MS);
    smsPoll.start();
  } else if (!smsNeed && smsPoll) {
    smsPoll.stop();
    smsPoll = null;
  }
}

// ---------- 卡详情 ----------
function settleFreezeWatch(ctx: H5Ctx, d: CardDetail): void {
  const w = freezeWatch;
  if (!w || w.id !== d.id) return;
  if (Date.now() - w.at > FREEZE_WATCH_MS) {
    freezeWatch = null;
    console.warn('[gp:cards] 冻结 / 解冻终态等待超时，放弃终态提示', d.id, d.status);
    return;
  }
  if (d.pending_action) {
    w.seen = true;
    return;
  }
  if (!w.seen && d.status === w.from) return; // 受理后读模型尚未出现在途标记
  freezeWatch = null;
  const t = T(ctx);
  if (d.status === w.from) ctx.gp.ui.toast(str(t, 'toastActionFailed'), 'error');
  else ctx.gp.ui.toast(str(t, d.status === 'INACTIVE' ? 'toastFrozen' : 'toastUnfrozen'), 'success');
}

async function loadDetail(ctx: H5Ctx, first: boolean): Promise<void> {
  const id = S(ctx).c.curId;
  if (id === null) return;
  const my = gen;
  const same = (): boolean => my === gen && S(ctx).c.curId === id && S(ctx).screen === 'cardDetail';
  const detailP = ctx.gp.api
    .call<CardDetail>('GET /api/user/cards/{id}', { path: { id } })
    .then((detail) => {
      if (!same()) return;
      settleFreezeWatch(ctx, detail);
      setC(ctx, { detail });
    })
    .catch((e: unknown) => {
      if (!same()) return;
      if (first) ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
      else console.warn('[gp:cards] 卡详情刷新失败', e);
    });
  if (!first) {
    await detailP;
    managePolls(ctx);
    return;
  }
  await Promise.all([detailP, loadCardTx(ctx, id)]);
  if (!same()) return;
  managePolls(ctx);
}

// U-05 本卡交易（U-22 E-04：每页 20、骨架 / 加载更多 / 没有更多了 / 首屏失败重试）
async function loadCardTx(ctx: H5Ctx, id: number): Promise<void> {
  const my = gen;
  const cardTx = new CursorList<BillEntry>((page) =>
    ctx.gp.api.call<UserPage<BillEntry>>('GET /api/user/cards/{id}/transactions', { path: { id }, query: { page, size: CARD_TX_SIZE } }),
  );
  setC(ctx, { cardTx });
  await cardTx.loadFirst();
  if (my !== gen || S(ctx).c.cardTx !== cardTx) return;
  setC(ctx, { billsRev: S(ctx).c.billsRev + 1 });
  if (cardTx.error) ctx.gp.ui.toast(str(T(ctx), 'errNetwork'), 'error');
}

async function loadMoreCardTx(ctx: H5Ctx): Promise<void> {
  const list = S(ctx).c.cardTx;
  if (!list || !list.hasMore) return;
  await ctx.gp.ui.run('cardTxMore', async () => {
    await list.loadMore();
    if (S(ctx).c.cardTx !== list) return;
    setC(ctx, { billsRev: S(ctx).c.billsRev + 1 });
    if (list.error) ctx.gp.ui.toast(str(T(ctx), 'errNetwork'), 'error'); // 加载更多失败：toast，按钮保留可重试
  });
}

async function loadSms(ctx: H5Ctx): Promise<void> {
  const id = S(ctx).c.curId;
  if (id === null) return;
  const my = gen;
  const list = await ctx.gp.api.call<SmsCode[]>('GET /api/user/cards/{id}/sms-codes', { path: { id } });
  if (my !== gen || S(ctx).c.curId !== id || S(ctx).screen !== 'cardDetail') return;
  const before = new Set(S(ctx).c.sms.map((x) => x.id));
  if (before.size > 0 && list.some((x) => !before.has(x.id))) {
    try {
      getTg()?.HapticFeedback?.impactOccurred('light'); // U-05：新码轻震
    } catch {
      // 旧版客户端无 HapticFeedback：触感非关键路径
    }
  }
  setC(ctx, { sms: list });
}

function openCardDetail(ctx: H5Ctx, id: number, from: Screen): void {
  const s = S(ctx);
  ctx.host.setState({ screen: 'cardDetail', c: { ...s.c, curId: id, detail: null, cardTx: null, secret: null, reveal: false, sms: [] }, sh: { ...s.sh, navFrom: from } });
  void loadDetail(ctx, true);
}

// D17：明文仅内存；已开 2FA 经 U-21 取码（gp.totp.run），未开直接请求（无 totp 参数）
async function getSecret(ctx: H5Ctx): Promise<CardSecret | null> {
  const s = S(ctx);
  const id = s.c.curId;
  if (id === null) return null;
  if (s.c.secret) return s.c.secret;
  if (revealing) return null;
  revealing = true;
  try {
    const sec = await ctx.gp.totp.run((totp) => ctx.gp.api.call<CardSecret>('GET /api/user/cards/{id}/secret', { path: { id }, query: { totp } }));
    if (!sec || S(ctx).c.curId !== id || S(ctx).screen !== 'cardDetail') return null;
    setC(ctx, { secret: sec });
    return sec;
  } catch (e) {
    ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
    return null;
  } finally {
    revealing = false;
  }
}

// U-05 前置条件：在途 → errCardInflight；状态不满足 → errCardState
function cardGuard(ctx: H5Ctx, allowed: readonly string[]): CardDetail | null {
  const d = S(ctx).c.detail;
  const t = T(ctx);
  if (!d) return null;
  if (d.pending_action) {
    ctx.gp.ui.toast(str(t, 'errCardInflight'), 'error');
    return null;
  }
  if (!allowed.includes(d.status)) {
    ctx.gp.ui.toast(str(t, 'errCardState'), 'error');
    return null;
  }
  return d;
}

function errVars(ctx: H5Ctx): Readonly<Record<string, string>> {
  const s = S(ctx);
  const L = s.sh.config?.limits ?? {};
  const sp = (s.c.products ?? []).find((p) => p.id === s.c.selected);
  return {
    min_amount: sp ? money2(sp.min_amount) : '',
    max_cards: L.max_cards === undefined ? '' : String(L.max_cards),
    fee_owed: s.sh.profile ? cfgMoney(s.sh.profile.fee_owed) : '',
  };
}

function handleWriteError(ctx: H5Ctx, e: unknown): void {
  if (e instanceof ApiError && e.code === 1203) {
    ctx.gp.ui.openModal({ type: 'kycGuide', title: str(T(ctx), 'kycNeedTitle') }); // G-09 / U-04a
    return;
  }
  // 开卡 4220 field=holder.*（含 area=US 州 / 城市 / 邮编号段）→ 中文字段提示，不回显后端通用「参数不合法」
  const field = e instanceof ApiError && e.code === 4220 ? e.data.field : undefined;
  const holderMsg = field ? holderFieldError(T(ctx), field, S(ctx).c.form, S(ctx).lang) : null;
  if (holderMsg) return ctx.gp.ui.toast(holderMsg, 'error');
  ctx.gp.ui.toast(ctx.gp.ui.err(e, errVars(ctx)), 'error');
}

async function openAmountModal(ctx: H5Ctx, type: 'recharge' | 'withdraw'): Promise<void> {
  const d = cardGuard(ctx, type === 'recharge' ? ['ACTIVE'] : ['ACTIVE', 'INACTIVE']);
  if (!d) return;
  if (ctx.gp.gate.guard()) return; // U-24：未开通邮箱登录不打开增加额度 / 卡转出弹层，改弹引导
  await ctx.gp.ui.run('modal', async () => {
    try {
      if (type === 'recharge') {
        const w = await ctx.gp.api.call<Wallet>('GET /api/user/wallet');
        setC(ctx, { balance: w.balance, modalAmount: '' });
      } else {
        const f = await ctx.gp.api.call<CardWithdrawFees>('GET /api/user/wallet/fees');
        setC(ctx, { withdrawFee: f.default.card_withdraw_fee, modalAmount: '' });
      }
      if (S(ctx).c.curId !== d.id || S(ctx).screen !== 'cardDetail') return;
      ctx.gp.ui.openModal({ type, title: str(T(ctx), type === 'recharge' ? 'rechargeTitle' : 'withdrawTitle') });
    } catch (e) {
      ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
    }
  });
}

function openClose(ctx: H5Ctx): void {
  const d = cardGuard(ctx, ['ACTIVE', 'INACTIVE']);
  if (!d) return;
  if (ctx.gp.gate.guard()) return; // U-24：销卡退款入钱包同属资金动作
  ctx.gp.ui.openModal({ type: 'close', title: str(T(ctx), 'closeTitle') });
}

async function toggleFreeze(ctx: H5Ctx): Promise<void> {
  const cur = S(ctx).c.detail;
  if (!cur?.capabilities.freeze) return;
  const d = cardGuard(ctx, ['ACTIVE', 'INACTIVE']);
  if (!d) return;
  const route = d.status === 'INACTIVE' ? 'POST /api/user/cards/{id}/unfreeze' : 'POST /api/user/cards/{id}/freeze';
  await ctx.gp.ui.run('freeze', async () => {
    try {
      await ctx.gp.api.call<CardSubmit>(route, { path: { id: d.id }, body: {} });
      freezeWatch = { id: d.id, from: d.status, seen: false, at: Date.now() };
      ctx.gp.ui.toast(str(T(ctx), 'toastSubmitted'), 'success');
      await loadDetail(ctx, false); // R-12：不做乐观更新，以读模型为准
    } catch (e) {
      handleWriteError(ctx, e);
    }
  });
}

async function confirmModal(ctx: H5Ctx): Promise<void> {
  const s = S(ctx);
  const t = T(ctx);
  const d = s.c.detail;
  const type = s.modal?.type;
  if (!d || !type) return;
  if (type === 'close') {
    await ctx.gp.ui.run('modal', async () => {
      try {
        await ctx.gp.api.call<CardSubmit>('POST /api/user/cards/{id}/close', { path: { id: d.id }, body: {} });
        ctx.gp.ui.closeModal();
        ctx.gp.ui.toast(str(t, 'toastClose'), 'success');
        ctx.gp.ui.go('home'); // 进入首页时由 update 刷新卡列表并按需轮询
      } catch (e) {
        handleWriteError(ctx, e);
      }
    });
    return;
  }
  if (type !== 'recharge' && type !== 'withdraw') return;
  const amt = parseDecimalInput(s.c.modalAmount, 2);
  if (amt === null || isZero(toMicro(amt))) return ctx.gp.ui.toast(str(t, 'errAmount'), 'error');
  const a = toMicro(amt);
  if (type === 'recharge') {
    if (cmp(a, toMicro('1')) < 0) return ctx.gp.ui.toast(str(t, 'errCardAmtMin'), 'error'); // Q-14
    if (cmp(a, micro(s.c.balance)) > 0) return ctx.gp.ui.toast(str(t, 'errBalance'), 'error');
  } else {
    // 方案 A：快照未核对不在前端拦截——服务端落单前即时核对（交易推导），冷静期 / 仍未核对时以 1207 说明原因。
    if (cmp(a, micro(s.c.withdrawFee)) <= 0) return ctx.gp.ui.toast(str(t, 'errCardWdFee').replace('{fee}', feeText(s.c.withdrawFee)), 'error');
    if (cmp(a, micro(wdMaxOf(d))) > 0) return ctx.gp.ui.toast(str(t, 'errWdMax').replace('{max}', money2(wdMaxOf(d))), 'error');
  }
  const route = type === 'recharge' ? 'POST /api/user/cards/{id}/recharge' : 'POST /api/user/cards/{id}/withdraw';
  await ctx.gp.ui.run('modal', async () => {
    try {
      await ctx.gp.api.call<CardSubmit>(route, { path: { id: d.id }, body: { amount: amt } });
      ctx.gp.ui.closeModal();
      ctx.gp.ui.toast(str(t, type === 'recharge' ? 'toastRecharge' : 'toastWithdraw'), 'success');
      await loadDetail(ctx, false);
    } catch (e) {
      handleWriteError(ctx, e);
    }
  });
}

// ---------- 选卡段 / 开卡 ----------
function selectedProduct(s: H5State): CardProduct | undefined {
  return (s.c.products ?? []).find((p) => p.id === s.c.selected);
}

function goProducts(ctx: H5Ctx): void {
  const s = S(ctx);
  if (s.sh.config?.switches.open_card === false) return ctx.gp.ui.toast(str(T(ctx), 'errSwitchOff'), 'error'); // G-08
  ctx.gp.ui.go('products');
  if (s.c.products) return;
  const my = gen;
  ctx.gp.api
    .call<CardProduct[]>('GET /api/user/card-products')
    .then((products) => {
      if (my === gen) setC(ctx, { products });
    })
    .catch((e: unknown) => {
      if (my === gen) ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
    });
}

function goOpenForm(ctx: H5Ctx): void {
  const s = S(ctx);
  const sp = selectedProduct(s);
  if (!sp) return;
  if (s.sh.config?.switches.open_card === false) return ctx.gp.ui.toast(str(T(ctx), 'errSwitchOff'), 'error');
  if (ctx.gp.gate.guard()) return; // U-24：可浏览卡段，选定开卡改弹引导
  // U-04 默认首充 50；50 < min_amount 时取 min_amount
  if (s.c.form.amount === '') setC(ctx, { form: { ...s.c.form, amount: cmp(toMicro('50'), micro(sp.min_amount)) < 0 ? plain(sp.min_amount) : '50' } });
  ctx.gp.ui.go('openForm');
}

async function submitOpen(ctx: H5Ctx): Promise<void> {
  const s = S(ctx);
  const t = T(ctx);
  const sp = selectedProduct(s);
  if (!sp) return;
  const f = s.c.form;
  if (!HOLDER_RE.test(f.first.trim()) || !HOLDER_RE.test(f.last.trim())) return ctx.gp.ui.toast(str(t, 'errHolder'), 'error');
  if (isUsArea(sp.area)) {
    const addrErr = usAddrError(t, f, s.lang);
    if (addrErr) return ctx.gp.ui.toast(addrErr, 'error');
  } else if (![f.street, f.city, f.state, f.zip].every((x) => x.trim())) return ctx.gp.ui.toast(str(t, 'errAddr'), 'error');
  const amt = parseDecimalInput(f.amount, 2);
  if (amt === null || isZero(toMicro(amt))) return ctx.gp.ui.toast(str(t, 'errAmount'), 'error');
  if (cmp(toMicro(amt), micro(sp.min_amount)) < 0) return ctx.gp.ui.toast(str(t, 'errMinAmount').replace('{min_amount}', money2(sp.min_amount)), 'error');
  if (sp.int_amount_only && !/^\d+$/.test(amt)) return ctx.gp.ui.toast(str(t, 'errIntAmount'), 'error');
  if (cmp(toMicro(sp.open_fee) + toMicro(amt), micro(s.c.balance)) > 0) return ctx.gp.ui.toast(str(t, 'errBalance'), 'error');
  if (ctx.gp.gate.guard()) return; // U-24
  await ctx.gp.ui.run('open', async () => {
    try {
      // 0045 代理加价：expected_open_fee = 本页展示的开卡费（产品列表 open_fee 原值，已是用户生效价）；服务端价格已变则 1214，不扣费不落单
      const r = await ctx.gp.api.call<CardSubmit>('POST /api/user/cards', {
        body: {
          product_id: sp.id,
          holder: { first: f.first.trim(), last: f.last.trim(), street: f.street.trim(), city: f.city.trim(), state: f.state.trim(), zip: f.zip.trim() },
          amount: amt,
          expected_open_fee: sp.open_fee,
        },
      });
      const cur = S(ctx);
      const rest = (cur.c.cards ?? []).filter((x) => x.id !== r.card?.id);
      const cards = r.card ? [r.card, ...rest] : cur.c.cards;
      // 回首页：update 进入 home 时刷新 GET /cards（card 为 null 时即重拉）并按 PENDING 启动 15s 轮询
      ctx.host.setState({ screen: 'home', c: { ...cur.c, cards, selected: null, feeOpen: null, form: F0 } });
      ctx.gp.ui.toast(str(t, 'toastOpen'), 'success');
    } catch (e) {
      if (e instanceof ApiError && e.code === 1214) await onOpenFeeChanged(ctx, sp.id, e);
      else handleWriteError(ctx, e);
    }
  });
}

// 合法非负十进制金额串原样返回，缺省 / 畸形 / 负数返回 null
function feeOrNull(v: string | null | undefined): string | null {
  if (typeof v !== 'string' || v === '') return null;
  try {
    return toMicro(v) < 0n ? null : v;
  } catch {
    return null;
  }
}

// 1214 OpenFeeChanged：开卡费已按所属代理生效价调整。先以 data.open_fee 就地更新所选卡段（openTotal 随之重算），
// 再重拉 GET /card-products 取权威价；停留开卡确认页由用户核对后再点提交（不自动重提，避免未经确认按新价扣费）。
// 卡段刷新后已不在列表（下架 / 售罄）→ errProductOff 并回选卡段页。仍在 busyOpen 内执行，刷新期间提交按钮保持互斥。
async function onOpenFeeChanged(ctx: H5Ctx, productId: number, e: ApiError): Promise<void> {
  const my = gen;
  const hinted = feeOrNull(e.data.open_fee);
  if (hinted === null) console.warn('[gp:cards] 1214 缺少合法 data.open_fee，改以刷新后的卡段价提示', e.data.open_fee);
  const patch = (list: readonly CardProduct[] | null): CardProduct[] | null =>
    list === null ? null : list.map((p) => (p.id === productId && hinted !== null ? { ...p, open_fee: hinted } : p));
  setC(ctx, { products: patch(S(ctx).c.products) });
  let refreshed: string | null = null;
  let gone = false;
  try {
    const products = await ctx.gp.api.call<CardProduct[]>('GET /api/user/card-products');
    if (my !== gen) return;
    setC(ctx, { products });
    const hit = products.find((p) => p.id === productId);
    refreshed = feeOrNull(hit?.open_fee);
    gone = hit === undefined;
  } catch (err) {
    if (my !== gen) return;
    console.warn('[gp:cards] 1214 后卡段刷新失败，沿用 data.open_fee', err);
  }
  const t = T(ctx);
  if (gone) {
    ctx.gp.ui.toast(str(t, 'errProductOff'), 'error');
    if (S(ctx).screen === 'openForm') ctx.gp.ui.go('products');
    return;
  }
  const fee = refreshed ?? hinted;
  ctx.gp.ui.toast(fee === null ? e.message : str(t, 'errOpenFeeChanged').replace('{fee}', feeText(fee)), 'error');
}

// ---------- 条款条幅（S-60） ----------
const termsKey = (uid: string): string => `gpu_terms_seen_${uid}`;

// ---------- vals ----------
function cardsVals(ctx: H5Ctx): Record<string, unknown> {
  const { t } = ctx;
  const s = ctx.s;
  const lang = ctx.lang;
  const c = s.c;
  const all = sortDesc(c.cards ?? []);
  const carousel = all.filter((x) => !HIDDEN_IN_CAROUSEL.has(x.status));
  const totalKnown = carousel.every((x) => x.spent_verified || x.status === 'PENDING');
  const total = carousel.reduce((a, x) => a + micro(x.available), 0n);
  // 2026-10-15 用户反馈：冷静期内不再只给「同步中」——spent / available 已是本地交易推导的估算值（cardq.exposedSpent 取
  // max(上游快照, 本地交易估算)），照常显示金额，旁边灰字「待核对」；核对完成（spent_verified）后去掉标记。体验评审 #6：不出 ≤ / ≥ 符号
  const unverified = (x: CardItem): boolean => !x.spent_verified && x.status !== 'PENDING';
  const balanceText = (x: CardItem): string => '$' + money2(x.available);
  const pendingTag = str(t, 'spentPending');
  const bills = c.bills;
  const uid = s.sh.profile?.uid ?? '';
  const sp = selectedProduct(s);
  const formAmt = inputMicro(c.form.amount);
  const openTotal = sp ? money2(fromMicro(toMicro(sp.open_fee) + formAmt)) : '0.00';
  const minM = sp ? micro(sp.min_amount) : 0n;
  const d = c.detail && c.detail.id === c.curId ? c.detail : null;
  const readonly = curReadonly(s);
  const ops = !!d && !NO_OPS.has(d.status);
  const showFreeze = !!d?.capabilities.freeze;
  const showClose = !!d?.capabilities.close;
  const sec = c.reveal ? c.secret : null;
  const mType = s.modal?.type ?? '';
  const mAmt = inputMicro(c.modalAmount);
  const wFee = micro(c.withdrawFee);
  const modalTotal = money2(fromMicro(mType === 'withdraw' ? withdrawNet(mAmt, wFee) : mAmt));
  const setForm = (k: Exclude<keyof OpenForm, 'cityManual'>) => (e: InputEvent) => setC(ctx, { form: { ...S(ctx).c.form, [k]: e.target.value } });
  const addrUs = !!sp && isUsArea(sp.area);
  const usForm = (next: (f: OpenForm, v: string) => OpenForm) => (e: InputEvent) => setC(ctx, { form: next(S(ctx).c.form, e.target.value) });
  const copySecret = (pick: (x: CardSecret) => string) => () => {
    if (readonly) return; // U-02a 只读态卡号 / CVV 无复制
    const have = S(ctx).c.secret;
    if (have) return ctx.gp.ui.copy(pick(have));
    void getSecret(ctx).then((x) => {
      if (x) ctx.gp.ui.copy(pick(x)); // E-06：复制后展示仍保持隐藏
    });
  };
  // U-02a：只读态卡号 / CVV 行恒脱敏且不渲染复制按钮
  const fields: Array<readonly [string, string, () => void, boolean?]> = d
    ? [
        [str(t, 'cardNo'), sec ? groupNo(sec.card_number) : maskNo(d.last4), copySecret((x) => x.card_number), !readonly],
        [str(t, 'expiry'), d.expiry ?? '--/--', () => ctx.gp.ui.copy(d.expiry ?? '')],
        [str(t, 'cvv'), sec ? sec.cvv : '***', copySecret((x) => x.cvv), !readonly],
        [str(t, 'holder'), `${d.holder_first} ${d.holder_last}`.trim(), () => ctx.gp.ui.copy(`${d.holder_first} ${d.holder_last}`.trim())],
        [str(t, 'street'), d.holder_street, () => ctx.gp.ui.copy(d.holder_street)],
        [str(t, 'city'), d.holder_city, () => ctx.gp.ui.copy(d.holder_city)],
        [str(t, 'state'), d.holder_state, () => ctx.gp.ui.copy(d.holder_state)],
        [str(t, 'zip'), d.holder_zip, () => ctx.gp.ui.copy(d.holder_zip)],
        [str(t, 'region'), countryName(d.holder_country, lang), () => ctx.gp.ui.copy(countryName(d.holder_country, lang))],
      ]
    : [];
  const open = (e: BillEntry): void => openBill(ctx, e);
  const ctf = listFlags(c.cardTx, CARD_TX_SIZE);
  return {
    isHome: s.screen === 'home',
    isMyCards: s.screen === 'myCards',
    isProducts: s.screen === 'products',
    isOpenForm: s.screen === 'openForm',
    isCardDetail: s.screen === 'cardDetail',
    booting: s.booting,
    // U-02 维护条（TD-835：数据源 config.switches）
    maintOn: !!s.sh.config?.switches.maint,
    maintText: s.sh.config?.switches.maint_text ?? '',
    termsBannerOn: s.screen === 'home' && s.sh.termsBanner && !c.termsClosed && !!uid && readLocal(termsKey(uid)) !== '1',
    termsBannerParts: legalParts(str(t, 'termsBanner'), s.sh.config?.site_url ?? '', lang),
    closeTermsBanner: () => {
      const id = S(ctx).sh.profile?.uid;
      if (id) writeLocal(termsKey(id), '1');
      setC(ctx, { termsClosed: true });
    },
    // 体验评审 #5：大字只放金额；未同步完时另起一行灰字提示，不在 32px 大字里拼内部状态词
    totalCardBalance: '$' + fmtAmount(total, 2),
    totalSyncing: !totalKnown,
    totalSyncText: str(t, 'totalSyncText'),
    cardCount: carousel.length,
    cards: carousel.map((x) => ({
      label: x.product_name.toUpperCase(),
      balance: money2(x.available),
      balanceText: balanceText(x),
      balancePending: unverified(x),
      pendingTag,
      brand: brandText(x.brand),
      maskedNumber: maskNo(x.last4),
      expiry: x.expiry ?? '--/--',
      faceStyle: faceStyle(x, false),
      statusText: cardStatusText(t, x.status),
      statusStyle: statusStyle(x.status),
      open: () => openCardDetail(ctx, x.id, 'home'),
    })),
    // 原型 L95 容器样式；卡列表到达前不启用 scroll-snap：否则首屏仅有占位卡时浏览器以其为吸附目标，卡片插入后重新吸附到末位（E-02）
    carouselStyle: `display:flex;gap:14px;overflow-x:auto;padding:16px 20px 6px${c.cards === null ? '' : ';scroll-snap-type:x mandatory'}`,
    cardDots: carousel.map((_, i) => ({ style: i === Math.min(c.dotIndex, carousel.length - 1) ? DOT_ON : DOT_OFF })),
    onCarouselScroll: (e: ScrollEvent) => {
      const x = e.currentTarget?.scrollLeft ?? e.target?.scrollLeft ?? 0;
      const n = carousel.length;
      const idx = n === 0 ? 0 : Math.max(0, Math.min(n - 1, Math.round(x / CARD_STEP_PX)));
      if (idx !== S(ctx).c.dotIndex) setC(ctx, { dotIndex: idx });
    },
    // 域新增文案经 vals 下发：模板 t.* 只允许原型字典键（build 双语守卫）
    allCardsText: str(t, 'allCards'),
    myCardsTitle: str(t, 'myCards'),
    showAllCards: all.some((x) => HIDDEN_IN_CAROUSEL.has(x.status)),
    goMyCards: () => ctx.gp.ui.go('myCards'),
    myCards: all.map((x) => ({
      name: x.product_name,
      sub: `•••• ${x.last4 ?? '----'} · ${x.expiry ?? '--/--'}`,
      available: money2(x.available),
      availableText: balanceText(x),
      availablePending: unverified(x),
      pendingTag,
      faceStyle: `width:44px;height:28px;border-radius:6px;background:${faceOf(x.face_color)};flex:none`,
      chipStyle: chipStyle(x.status),
      statusText: cardStatusText(t, x.status),
      open: () => openCardDetail(ctx, x.id, 'myCards'),
    })),
    myCardsEmpty: c.cards !== null && c.cards.length === 0,
    goProducts: () => goProducts(ctx),
    goOpenForm: () => goOpenForm(ctx),
    goReferral: () => ctx.gp.ui.go('referral'),
    goSupport: () => ctx.gp.ui.go('support'),
    billFilters: (
      [
        ['all', str(t, 'all')],
        ['purchase', str(t, 'typePurchase')],
        ['other', str(t, 'typeRefund') + '/' + str(t, 'typeFee')],
      ] as const
    ).map(([k, label]) => ({ label, style: pill(c.filter === k), pick: () => void reloadBills(ctx, k) })),
    visibleTx: (bills?.items ?? []).map((e) => mapBill(t, e, open)),
    txEmpty: !!bills && !bills.firstLoading && bills.items.length === 0,
    hasMoreTx: !!bills && !bills.firstLoading && bills.items.length > 0 && bills.hasMore,
    loadMoreTx: () => {
      const b = S(ctx).c.bills;
      if (!b) return;
      void ctx.gp.ui.run('more', async () => {
        await b.loadMore();
        if (S(ctx).c.bills !== b) return;
        setC(ctx, { billsRev: S(ctx).c.billsRev + 1 });
        if (b.error) ctx.gp.ui.toast(str(T(ctx), 'errNetwork'), 'error');
      });
    },
    busyMore: s.busy === 'more',
    // U-03
    products: (c.products ?? []).map((p) => {
      const on = c.selected === p.id;
      return {
        region: regionName(p.area, lang),
        tag: p.supports_3ds ? '3DS' : str(t, 'no3ds'),
        brand: brandText(p.brand),
        bin: p.bin,
        openFee: money2(p.open_fee),
        // 用户反馈 2026-10-16：费率行过多，默认折叠为开卡费 + 销卡费，点「查看全部费率」展开（展开即选中该卡种）
        ...foldFees(t, feeRows(t, p), c.feeOpen === p.id),
        toggleFees: () => setC(ctx, { selected: p.id, feeOpen: c.feeOpen === p.id ? null : p.id }),
        selected: on,
        pick: () => setC(ctx, { selected: p.id }),
        // 原型 L702 逐字，height:190px → min-height:190px（费用行自适应）
        style: `position:relative;min-height:190px;border-radius:20px;padding:18px;color:#fff;background:${faceOf(p.face_color)};display:flex;flex-direction:column;justify-content:space-between;cursor:pointer;box-shadow:0 12px 28px rgba(15,26,23,.16);outline:${on ? '3px solid #39c88f' : '3px solid transparent'};outline-offset:3px;transition:transform .15s;transform:${on ? 'scale(1.01)' : 'none'}`,
      };
    }),
    selectedBinText: sp ? `${sp.bin} · ${brandText(sp.brand)} · ${regionName(sp.area, lang)}` : lang === 'zh' ? '未选择' : 'None',
    noProductSelected: !sp,
    openBtnStyle: `width:100%;height:50px;border:0;border-radius:14px;background:${sp ? '#0d9a68' : '#b9d9cb'};color:#fff;font-size:15px;font-weight:700;cursor:${sp ? 'pointer' : 'not-allowed'}`,
    selectedOpenFee: sp ? money2(sp.open_fee) : '0.00',
    // U-04
    billingHint: sp ? str(t, 'billingAddrHint').replace('{country}', regionName(sp.area, lang)) : '',
    minAmountHint: sp ? str(t, 'minAmountHint').replace('{min_amount}', money2(sp.min_amount)).replace('{int}', sp.int_amount_only ? str(t, 'minAmountIntSuffix') : '') : '',
    openTotal, // 合计扣款 = 开卡费 + 首充，从 USDT 余额扣（D2 1:1；用户拍板 2026-10-15 只写 USDT，原「扣除 USDT」重复行已合并）
    form: c.form,
    onForm: {
      first: setForm('first'), last: setForm('last'), street: setForm('street'), city: setForm('city'),
      // area=US：州为下拉（换州时清掉不属于新州的城市）、邮编只收数字截 5 位；其余地区自由输入
      state: addrUs ? usForm(pickState) : setForm('state'),
      zip: addrUs ? usForm((f, v) => ({ ...f, zip: zipDigits(v) })) : setForm('zip'),
      amount: setForm('amount'),
    },
    // U-04 美国账单地址（billaddr.ts）
    addrUs,
    onUsCity: usForm(pickCity),
    ...addrView(t, c.form, addrUs, lang),
    amountChips: CHIPS.map((v) => ({
      v,
      pick: () => setC(ctx, { form: { ...S(ctx).c.form, amount: String(v) } }),
      style: chip(c.form.amount === String(v), !!sp && cmp(BigInt(v) * 1_000_000n, minM) < 0),
      pickModal: () => setC(ctx, { modalAmount: String(v) }),
      styleModal: chip(c.modalAmount === String(v)),
    })),
    submitOpen: () => void submitOpen(ctx),
    busyOpen: s.busy === 'open',
    // U-05
    backFromDetail: () => ctx.gp.ui.go(S(ctx).sh.navFrom === 'myCards' ? 'myCards' : 'home'), // TD-834
    cur: d
      ? {
          balance: money2(d.available),
          balanceText: balanceText(d),
          balancePending: unverified(d),
          pendingTag,
          brand: brandText(d.brand),
          displayNumber: sec ? groupNo(sec.card_number) : maskNo(d.last4),
          displayCvv: sec ? sec.cvv : '***',
          expiry: d.expiry ?? '--/--',
          faceStyleBig: faceStyle(d, true),
          statusText: cardStatusText(t, d.status),
          statusStyle: statusStyle(d.status),
        }
      : { balance: '', balanceText: '', balancePending: false, pendingTag, brand: '', displayNumber: '', displayCvv: '', expiry: '', faceStyleBig: FACE_SKELETON, statusText: '', statusStyle: '' },
    spentUnverified: !!d && unverified(d),
    spentPendingHint: str(t, 'spentPendingHint'),
    detailReadonly: readonly,
    showDetailOps: ops,
    showFreeze,
    showClose,
    opsGridStyle: `display:grid;grid-template-columns:repeat(${2 + (showFreeze ? 1 : 0) + (showClose ? 1 : 0)},1fr);gap:8px;padding:16px 20px 0`,
    revealText: c.reveal ? str(t, 'hide') : str(t, 'reveal'),
    toggleReveal: () => {
      const cur = S(ctx).c;
      if (cur.reveal) return setC(ctx, { reveal: false });
      if (cur.secret) return setC(ctx, { reveal: true });
      void getSecret(ctx).then((x) => {
        if (x) setC(ctx, { reveal: true });
      });
    },
    freezeText: d?.status === 'INACTIVE' ? str(t, 'unfreeze') : str(t, 'freeze'),
    toggleFreeze: () => void toggleFreeze(ctx),
    limitRow: d
      ? [
          { label: str(t, 'limitTotal'), value: money2(d.limits.total), display: '$' + money2(d.limits.total), pending: false, pendingTag },
          { label: str(t, 'limitSpent'), value: money2(d.limits.spent), display: '$' + money2(d.limits.spent), pending: unverified(d), pendingTag },
          { label: str(t, 'limitAvailable'), value: money2(d.limits.available), display: '$' + money2(d.limits.available), pending: unverified(d), pendingTag },
        ]
      : [],
    cardFields: fields.map(([label, value, copy, canCopy = true]) => ({ label, value, copy, canCopy })),
    opsBtn: opsBtns(d),
    curTx: (c.cardTx?.items ?? []).map((e) => mapBill(t, e, open)),
    curTxEmpty: ctf.empty,
    curTxSkel: ctf.skel,
    curTxRetry: ctf.retry,
    retryCurTx: () => {
      const id = S(ctx).c.curId;
      if (id !== null) void loadCardTx(ctx, id);
    },
    curTxHasMore: ctf.more,
    loadMoreCurTx: () => void loadMoreCardTx(ctx),
    busyMoreCurTx: s.busy === 'cardTxMore',
    curTxNoMore: ctf.noMore,
    smsList: c.sms.map((x) => ({ code: x.code, merchant: x.merchant ?? '', time: fmtDateTime(x.created_at, { seconds: true }), copy: () => ctx.gp.ui.copy(x.code) })),
    smsEmpty: c.sms.length === 0,
    openRecharge: () => void openAmountModal(ctx, 'recharge'),
    openWithdraw: () => void openAmountModal(ctx, 'withdraw'),
    openClose: () => openClose(ctx),
    // U-06a～c
    modalAmount: c.modalAmount,
    onModalAmount: (e: InputEvent) => setC(ctx, { modalAmount: e.target.value }),
    showModalFee: mType === 'withdraw',
    // 卡转出「全部」：一键填入最多可转出金额（上限 > 0 时显示）
    showWdAll: mType === 'withdraw' && cmp(micro(wdMaxOf(d)), 0n) > 0,
    wdAllText: `${str(t, 'wdAll')} $${money2(wdMaxOf(d))}`,
    pickWdAll: () => setC(ctx, { modalAmount: money2(wdMaxOf(S(ctx).c.detail)) }),
    showModalLimit: mType === 'recharge', // 增加额度：卡侧额度行（$）与钱包扣款行（USDT）分开写（用户拍板 2026-10-15）
    modalFeeLabel: str(t, 'cardWdFee'),
    modalFee: mType === 'withdraw' ? feeText(c.withdrawFee) : '0.00',
    modalTotal,
    modalTotalUsdt: modalTotal,
    confirmModal: () => void confirmModal(ctx),
    busyModal: s.busy === 'modal',
    openDispute: () => void disputeTx(ctx),
    // U-04a
    kycGuideGo: () => {
      const pending = S(ctx).sh.profile?.kyc_status === 'pending';
      ctx.gp.ui.closeModal();
      if (!pending) ctx.gp.ui.go('kyc');
    },
  };
}

function cardsModal(ctx: H5Ctx): Record<string, unknown> {
  const { t, s } = ctx;
  const m = s.modal;
  const type = m?.type ?? '';
  const d = s.c.detail;
  const pending = s.sh.profile?.kyc_status === 'pending';
  const L = s.sh.config?.limits ?? {};
  const sub =
    type === 'recharge'
      ? `${str(t, 'rechargeSub')} · ${money2(s.c.balance)} USDT`
      : type === 'withdraw'
        ? `${str(t, 'withdrawSub')} · ${str(t, 'wdMaxText').replace('{max}', money2(wdMaxOf(d)))}${d && !(d.spent_verified || d.status === 'PENDING') ? ` · ${str(t, 'spentPending')}` : ''}`
        : type === 'close'
          ? str(t, 'closeSub').replace('{close_fee}', d ? money2(d.product.close_fee) : '0.00')
          : '';
  return {
    isTx: type === 'tx',
    isFeeTx: type === 'tx' && m?.isFee === true,
    isAmount: type === 'recharge' || type === 'withdraw',
    isConfirm: type === 'close',
    isKycGuide: type === 'kycGuide',
    sub,
    totalLabel: str(t, 'addLimit'), // 仅增加额度渲染（showModalLimit），值写 $
    usdtLabel: type === 'withdraw' ? str(t, 'creditUsdt') : str(t, 'walletDeduct'), // 钱包侧金额写 USDT；卡转出到账行与原「到账金额 $」合并
    kycGuideText: pending
      ? str(t, 'kycPendingTip')
      : str(t, 'kycNeedSub').replace('{kyc_limit}', cfgMoney(L.kyc_limit)).replace('{kyc_limit_verified}', cfgMoney(L.kyc_limit_verified)),
    kycGuideBtn: pending ? str(t, 'confirm') : str(t, 'goKyc'),
  };
}

function resetSession(ctx: H5Ctx): void {
  gen++;
  sessionOn = false;
  homeLoading = false;
  stopPolls();
  freezeWatch = null;
  revealing = false;
  setC(ctx, C0);
}

function refetchScreen(ctx: H5Ctx): void {
  if (!sessionOn) return;
  const s = S(ctx);
  // 轮询器在途时回前台已由 createPoller 立即补跑（TD-838），此处跳过同一查询避免重复请求
  if (s.screen === 'home') {
    if (!homeLoading && !homePoll) void loadCards(ctx, false);
    void reloadBills(ctx, s.c.filter);
  } else if (s.screen === 'myCards') {
    if (!homePoll) void loadCards(ctx, false);
  } else if (s.screen === 'cardDetail') {
    if (!detailPoll) void loadDetail(ctx, true);
    if (!smsPoll) void loadSms(ctx).catch((e: unknown) => console.warn('[gp:cards] 3DS 刷新失败', e));
  } else if (s.screen === 'products') {
    const my = gen;
    ctx.gp.api
      .call<CardProduct[]>('GET /api/user/card-products')
      .then((products) => {
        if (my === gen) setC(ctx, { products });
      })
      .catch((e: unknown) => console.warn('[gp:cards] 卡段刷新失败', e));
  } else if (s.screen === 'openForm') {
    void refreshBalance(ctx);
  }
}

export const cards: H5Domain = {
  name: 'cards',
  keys: KEYS,
  modalKeys: MODAL_KEYS,
  migrated: true,
  i18n: I18N_CARDS,
  rewrites: REWRITES_CARDS,
  state: () => ({ c: C0 }),
  mount(ctx) {
    gen++;
    sessionOn = false;
    homeLoading = false;
    stopPolls();
    unVisibility?.();
    unVisibility = null;
    ctx.events.on('session:ready', () => {
      resetSession(ctx);
      sessionOn = true;
      void loadHome(ctx);
    });
    // U-22 E-05：回前台立即重拉当前页查询（首页卡列表 + 账单、我的卡、卡详情 + 本卡交易 + 3DS、选卡段、开卡表单余额）
    unForeground?.();
    unForeground = ctx.gp.foreground.on(() => refetchScreen(ctx));
    // 06 U-12 通知跳转：卡类通知按 card_id 进卡详情（U-05）
    ctx.gp.nav.provide({
      openCard: (id) => {
        if (!sessionOn || !Number.isInteger(id) || id <= 0) return false;
        openCardDetail(ctx, id, 'home');
        return true;
      },
      // 充值成功页「去开卡」：与首页入口同一选卡段流程（G-08 开卡开关关时 toast 且不跳转，仍视为已处理）
      openProducts: () => {
        if (!sessionOn) return false;
        goProducts(ctx);
        return true;
      },
    });
    ctx.events.on('session:lost', () => {
      resetSession(ctx);
      if (S(ctx).booting) ctx.host.setState({ booting: false }); // 未进首页的会话失败路径（如 tg_auth_failed）兜底收起骨架
    });
    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      // U-05 / D17：页面切后台即丢弃卡明文并复位 reveal
      const onVis = (): void => {
        if (document.visibilityState === 'hidden' && S(ctx).c.secret) setC(ctx, { secret: null, reveal: false });
      };
      const doc = document; // 退订绑定挂载时的文档对象：卸载晚于全局 document 销毁时不抛错
      doc.addEventListener('visibilitychange', onVis);
      unVisibility = () => doc.removeEventListener('visibilitychange', onVis);
    }
  },
  update(ctx, prev) {
    const from = prev.screen;
    const to = S(ctx).screen;
    if (from === to) return;
    if (from === 'cardDetail') {
      setC(ctx, { secret: null, reveal: false, sms: [] }); // D17：离开卡详情明文失效
      freezeWatch = null; // 终态提示只在卡详情内给出
    }
    // 首屏在途时不重复拉取；首屏失败（cards 仍为 null）或开卡 202 未带 card 时，回首页即重拉自愈
    if ((to === 'home' || to === 'myCards') && sessionOn && !homeLoading) void loadCards(ctx, false);
    if (to === 'openForm') void refreshBalance(ctx);
    managePolls(ctx);
  },
  unmount() {
    gen++;
    stopPolls();
    unVisibility?.();
    unVisibility = null;
    unForeground?.();
    unForeground = null;
  },
  vals: cardsVals,
  modal: cardsModal,
};

// 测试专用：纯映射函数（生产代码不引用）
export const __test = { mapBill, faceStyle, statusStyle, feeRows, withdrawNet, cardStatusText, chipStyle };
